import type { AttachmentMode } from '../hooks/useClaimAttachments'

/* global Office */

export function isComposeMode(): boolean {
  try {
    const item = Office?.context?.mailbox?.item
    if (!item) return false
    return typeof item.subject !== 'string'
  } catch {
    return false
  }
}

export function getHasEmailAttachments(mode: AttachmentMode): boolean {
  if (mode !== 'save') return true
  try {
    const item = Office?.context?.mailbox?.item
    if (!item) return true
    return ((item as Office.MessageRead).attachments ?? []).length > 0
  } catch {
    return true
  }
}

// --- Simple Attach (Attach to MC1) -------------------------------------------------------------

export interface SimpleAttachAvailability {
  supported: boolean
  reason?: string
}

const SIMPLE_ATTACH_NO_ITEM_REASON = 'No email is selected'
const SIMPLE_ATTACH_COMPOSE_REASON = 'Only received emails can be attached to MC1'
const SIMPLE_ATTACH_HOST_REASON = 'This version of Outlook cannot supply the original email'

// getAsFileAsync needs Mailbox 1.14, but the manifest floor stays at 1.5 on purpose: raising it
// would stop the whole add-in loading on older hosts and on Outlook Mobile, which caps at 1.5.
// Checking at runtime instead means the button simply disables where the API is missing.
export function getSimpleAttachAvailability(): SimpleAttachAvailability {
  try {
    const item = Office?.context?.mailbox?.item
    if (!item) return { supported: false, reason: SIMPLE_ATTACH_NO_ITEM_REASON }
    if (isComposeMode()) return { supported: false, reason: SIMPLE_ATTACH_COMPOSE_REASON }

    // Classic Outlook for Windows is deliberately allowed, by product decision, even though its copy
    // can carry a different Message-ID (office-js#5635). See getSelectedMessageAsEml.
    const hasApi =
      Office.context.requirements?.isSetSupported('Mailbox', '1.14') &&
      typeof (item as Office.MessageRead).getAsFileAsync === 'function'
    if (!hasApi) return { supported: false, reason: SIMPLE_ATTACH_HOST_REASON }

    return { supported: true }
  } catch {
    return { supported: false, reason: SIMPLE_ATTACH_HOST_REASON }
  }
}

// Returns the open message as Base64-encoded EML — the original bytes, which is what the audit
// trail depends on. Known Outlook bug: on classic Outlook for Windows the returned headers differ
// from the real message, including Message-ID (office-js#5635, still open), so only new Outlook
// and OWA supply a faithful copy.
export function getSelectedMessageAsEml(): Promise<string> {
  return new Promise((resolve, reject) => {
    ;(Office.context.mailbox.item as Office.MessageRead).getAsFileAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result.value)
      else reject(new Error(result.error?.message || 'Failed to read the original email'))
    })
  })
}
